import { useEffect } from 'react'
import { MAP_DEPART_KEY, MAP_LAST_HIDDEN_KEY } from '../lib/directions'
import {
  getLastHiddenWallClockMs,
  getSawLifecycleBackgroundSinceMapMark,
  recordHiddenWallClock,
} from '../lib/mapResumeLifecycle'
import { resumeBreadcrumb } from '../lib/resumeTelemetry'

/** Walking between holes can exceed a few minutes. */
const STALE_MAP_MAX_MS = 45 * 60 * 1000
const RESUME_RELOAD_MIN_BG_MS = 400
/** When OS backgrounded the page but hidden timestamps never crossed `depart`. */
const DEPART_ONLY_MIN_MS = 4000
const LONG_BACKGROUND_HINT_MS = 60_000

function nudgeCompositing() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        if (document.body) void document.body.getBoundingClientRect()
        const root = document.getElementById('root')
        if (root) {
          root.style.transform = 'translateZ(0)'
          void root.offsetHeight
          root.style.removeProperty('transform')
        }
        const y = window.scrollY
        window.scrollTo(0, y === 0 ? 1 : y)
        window.scrollTo(0, y)
        window.dispatchEvent(new Event('resize'))
      } catch {
        // ignore
      }
    })
  })
}

/** Tab/app switch back from Maps without full unload — never navigate (404 risk); repaint hard. */
function nudgeCompositingBurst() {
  const run = () => nudgeCompositing()
  run()
  window.setTimeout(run, 80)
  window.setTimeout(run, 200)
  window.setTimeout(run, 360)
}

function clearMapResumeMarkersOnly() {
  try {
    sessionStorage.removeItem(MAP_DEPART_KEY)
    sessionStorage.removeItem(MAP_LAST_HIDDEN_KEY)
  } catch {
    // ignore
  }
}

function persistHiddenTimestamp() {
  const t = Date.now()
  recordHiddenWallClock(t)
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(MAP_LAST_HIDDEN_KEY, String(t))
    }
  } catch {
    // ignore
  }
}

function readDepartAndSessionHidden() {
  let depart = 0
  let sessionHidden = 0
  try {
    const d = sessionStorage.getItem(MAP_DEPART_KEY)
    const h = sessionStorage.getItem(MAP_LAST_HIDDEN_KEY)
    depart = d ? parseInt(d, 10) : 0
    sessionHidden = h ? parseInt(h, 10) : 0
  } catch {
    return { depart: 0, sessionHidden: 0 }
  }
  return { depart, sessionHidden }
}

function maybeDispatchLongBackgroundHint() {
  const mem = getLastHiddenWallClockMs()
  let sessionH = 0
  try {
    const raw = sessionStorage.getItem(MAP_LAST_HIDDEN_KEY)
    sessionH = raw ? parseInt(raw, 10) : 0
  } catch {
    return
  }
  const lastHidden = Math.max(
    Number.isFinite(sessionH) && sessionH > 0 ? sessionH : 0,
    Number.isFinite(mem) && mem > 0 ? mem : 0
  )
  if (!lastHidden) return
  const inBg = Date.now() - lastHidden
  if (inBg < LONG_BACKGROUND_HINT_MS) return
  try {
    if (sessionStorage.getItem('puttzin_bg_hint_dismissed') === '1') return
  } catch {
    return
  }
  try {
    window.dispatchEvent(
      new CustomEvent('puttzin-long-background-visible', {
        detail: { ms: inBg },
      })
    )
  } catch {
    // ignore
  }
}

function maybeReloadAfterMapReturn() {
  const { depart, sessionHidden } = readDepartAndSessionHidden()
  const memoryHidden = getLastHiddenWallClockMs()
  const effectiveHidden = Math.max(
    Number.isFinite(sessionHidden) && sessionHidden > 0 ? sessionHidden : 0,
    Number.isFinite(memoryHidden) && memoryHidden > 0 ? memoryHidden : 0
  )

  if (!depart || Number.isNaN(depart)) {
    resumeBreadcrumb('skip:no_depart')
    return
  }

  if (Date.now() - depart > STALE_MAP_MAX_MS) {
    try {
      sessionStorage.removeItem(MAP_DEPART_KEY)
    } catch {
      // ignore
    }
    resumeBreadcrumb('skip:stale_depart')
    return
  }

  if (effectiveHidden >= depart) {
    const inBackgroundMs = Date.now() - effectiveHidden
    if (inBackgroundMs < RESUME_RELOAD_MIN_BG_MS) {
      resumeBreadcrumb('skip:short_background', { inBackgroundMs })
      return
    }
    resumeBreadcrumb('maps_return_soft_nudge')
    nudgeCompositingBurst()
    clearMapResumeMarkersOnly()
    return
  }

  if (
    getSawLifecycleBackgroundSinceMapMark() &&
    Date.now() - depart >= DEPART_ONLY_MIN_MS &&
    Date.now() - depart <= STALE_MAP_MAX_MS
  ) {
    resumeBreadcrumb('maps_return_depart_only_soft_nudge')
    nudgeCompositingBurst()
    clearMapResumeMarkersOnly()
    return
  }

  resumeBreadcrumb('skip:no_effective_hidden', {
    effectiveHidden,
    sessionHidden,
    memoryHidden,
    sawBg: getSawLifecycleBackgroundSinceMapMark(),
  })
}

function onForeground() {
  nudgeCompositing()
  maybeDispatchLongBackgroundHint()
  maybeReloadAfterMapReturn()
}

/**
 * @see `src/lib/directions.js` for map-open markers
 */
export function useAppResumeRecovery() {
  // After same-tab `location.assign` to Maps then Back: document is fresh;
  // avoid programmatic `reload`/`replace`/`assign` (caused intermittent Vercel
  // NOT_FOUND on some phones). Repaint hard + clear markers; footer "Refresh app"
  // remains the full recovery if the GPU layer stays blank.
  useEffect(() => {
    try {
      const navEntry = performance.getEntriesByType('navigation')[0]
      const navType =
        navEntry && 'type' in navEntry ? /** @type {PerformanceNavigationTiming} */ (navEntry).type : ''
      if (navType !== 'back_forward') return
      const { depart } = readDepartAndSessionHidden()
      if (!depart || Number.isNaN(depart)) return
      if (Date.now() - depart > STALE_MAP_MAX_MS) {
        try {
          sessionStorage.removeItem(MAP_DEPART_KEY)
        } catch {
          // ignore
        }
        return
      }
      resumeBreadcrumb('same_tab_back_soft_recovery')
      nudgeCompositingBurst()
      clearMapResumeMarkersOnly()
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    const onPageShow = (e) => {
      nudgeCompositing()
      if (e.persisted) {
        resumeBreadcrumb('bfcache_soft_recovery')
        nudgeCompositingBurst()
        clearMapResumeMarkersOnly()
      }
    }

    const onHidden = () => {
      persistHiddenTimestamp()
      resumeBreadcrumb('lifecycle_hidden')
    }

    const onPageHide = () => {
      onHidden()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        resumeBreadcrumb('visibility_visible')
        onForeground()
      } else {
        onHidden()
      }
    }

    const onFocus = () => {
      nudgeCompositing()
    }

    const onFreeze = () => {
      persistHiddenTimestamp()
      resumeBreadcrumb('lifecycle_freeze')
    }

    const onResume = () => {
      resumeBreadcrumb('lifecycle_resume')
      onForeground()
    }

    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility, { passive: true })
    document.addEventListener('freeze', onFreeze, { passive: true })
    document.addEventListener('resume', onResume, { passive: true })

    return () => {
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('freeze', onFreeze)
      document.removeEventListener('resume', onResume)
    }
  }, [])
}
